import type { Campaign } from 'c-sdk';
import { NetworkRequestState } from 'c-data-layer';

/**
 * The PCA client/internal controls on the campaign overview (admins with
 * `admin.campaign.pca_client_generation`). "live" is the client-facing PCA, "sandbox" the
 * internal one; `pca_available[env]` is when that PCA was last generated.
 */

export type PcaAction = 'run' | 'download' | 'delete' | 'publish';
export type PcaEnvironment = 'live' | 'sandbox';

/** What each control is allowed to do for one environment. */
export const pcaEnvironmentControls = (
    campaign: Pick<Campaign, 'pca_available'> | null | undefined,
    environment: PcaEnvironment,
    insertionOrdersMissing: boolean,
) => {
    const generatedAt = campaign?.pca_available?.[environment] || null;

    return {
        // a DSP campaign can't be built until its insertion orders are picked
        runDisabled: insertionOrdersMissing,
        downloadDisabled: !generatedAt,
        deleteDisabled: !generatedAt,
        // only an internal PCA that exists can be published to the client
        showPublish: environment === 'sandbox' && !!generatedAt,
        generatedAt,
    };
};

/** The arguments each action's request is started with. */
export const pcaActionArgs = (action: PcaAction, environment: PcaEnvironment, id: number) => {
    switch (action) {
        case 'download': {
            const formData = new FormData();
            formData.append('environment', environment);
            return [Number(id), formData, { responseType: 'blob' }] as const;
        }
        case 'run':
            return [{ id, environment, consumerType: 'full' }] as const;
        case 'delete':
            return [{ id, environment }] as const;
        case 'publish':
        default:
            return [{ id }] as const;
    }
};

/** How long a finished action keeps its tick or its error before resetting. */
export const pcaActionResetDelay = (failed: boolean) => (failed ? 10000 : 2000);

/** A button is busy while running, shows a tick once done, and can't be pressed in either state. */
export const pcaActionButtonState = (requestState: NetworkRequestState, disabled?: boolean) => {
    const isLoading = requestState === NetworkRequestState.InProgress;
    const isSuccess = requestState === NetworkRequestState.Success;
    return { isLoading, isSuccess, disabled: isLoading || isSuccess || !!disabled };
};

/** `dd/MM/yyyy HH:mm TZ` in the viewer's own time zone. */
export const formatPcaGeneratedAt = (dateString: string) => {
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    const timeZoneCode = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
        .formatToParts(date)
        .find(part => part.type === 'timeZoneName')?.value;

    return `${day}/${month}/${year} ${hours}:${minutes} ${timeZoneCode}`;
};
